import React, { useEffect, useState, useRef } from "react";

interface ContextMenuPosition {
  x: number;
  y: number;
}

interface ContextMenuHook {
  contextMenuVisible: boolean;
  contextMenuPosition: ContextMenuPosition;
  contextMenuRef: React.RefObject<HTMLDivElement>;
  handleContextMenu: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  handleSetContextMenuVisible: (value: boolean) => void;
}

const useContextMenu = (onContextMenu?: () => void): ContextMenuHook => {
  const [contextMenuVisible, setContextMenuVisible] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
  const contextMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleCloseContextMenu = (event: { button: number }) => {
      if (event.button === 2) setContextMenuVisible(false);
    };

    document.body.addEventListener("mousedown", handleCloseContextMenu);
    return () => document.body.removeEventListener("mousedown", handleCloseContextMenu);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target as Node)) {
        setContextMenuVisible(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleContextMenu = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.preventDefault();
    if (onContextMenu) onContextMenu();
    setContextMenuPosition({ x: event.clientX, y: event.clientY });
    setContextMenuVisible(true);
  };

  const handleSetContextMenuVisible = (value: boolean) => {
    setContextMenuVisible(value);
  };

  return {
    contextMenuVisible,
    contextMenuPosition,
    contextMenuRef,
    handleContextMenu,
    handleSetContextMenuVisible
  };
};

export default useContextMenu;
